import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Modal, Button, Table, Tooltip, Typography, Space, Input, message, Tag, Row, Col } from 'antd';
import { MessageOutlined, MailOutlined, SendOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const SendCustomMessageModal = ({ visible, onCancel, onConfirm, selectedOrderItems, loading }) => {
    const [messageContent, setMessageContent] = useState('');
    const [sending, setSending] = useState(false);
    const textAreaRef = useRef(null);

    // Group order items by shopifyOrderId to show unique orders
    const groupedOrders = useMemo(() => {
        if (!selectedOrderItems || selectedOrderItems.length === 0) return [];

        const orderMap = new Map();
        
        selectedOrderItems.forEach(item => {
            const shopifyOrderId = item.order?.shopifyOrderId || item.shopifyOrderId;
            const shopifyOrderName = item.order?.shopifyOrderName || item.shopifyOrderName;
            if (!shopifyOrderId) return;

            if (!orderMap.has(shopifyOrderId)) {
                orderMap.set(shopifyOrderId, {
                    shopifyOrderId,
                    shopifyOrderName,
                    orderItems: [],
                    customerName: item.order?.shippingName || item.shippingName || 'Unknown',
                    customerEmail: item.order?.email || item.email || 'Unknown',
                    sku: item.sku || 'Unknown',
                    title: item.title || 'Unknown Product',
                    storeName: item.order?.store?.storeName || 'Store'
                });
            }
            
            orderMap.get(shopifyOrderId).orderItems.push(item);
        });

        return Array.from(orderMap.values());
    }, [selectedOrderItems]);

    const handleSendMessage = async () => {
        if (!messageContent.trim()) {
            message.error('Please enter a message');
            return;
        }

        if (messageContent.length > 5000) {
            message.error('Message content cannot exceed 5000 characters');
            return;
        }

        setSending(true);
        try {
            const orderItemIds = selectedOrderItems.map(item => item.id);
            
            const result = await onConfirm({
                orderItemIds,
                messageContent: messageContent.trim()
            });

            if (result && result.success) {
                message.success(`Custom message will be sent to ${groupedOrders.length} orders via email!`);
                setMessageContent('');
            } else {
                throw new Error(result?.message || 'Failed to send custom message');
            }
        } catch (error) {
            console.error('Error sending custom message:', error);
            message.error(error.message || 'Failed to send custom message');
        } finally {
            setSending(false);
        }
    };

    const handleCancel = () => {
        setMessageContent('');
        setSending(false);
        if (onCancel) {
            onCancel();
        }
    };

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 40,
            align: 'center',
            render: (_, record, index) => (
                <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{index + 1}</Text>
            )
        },
        {
            title: 'Order',
            dataIndex: 'shopifyOrderName',
            key: 'shopifyOrderName',
            width: 100,
            render: (text) => (
                <Tag color="green" style={{ fontSize: 11 }}>{text}</Tag>
            )
        },
        {
            title: 'Store',
            dataIndex: 'storeName',
            key: 'storeName',
            width: 100,
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <Text style={{ fontSize: 12 }}>{text}</Text>
                </Tooltip>
            )
        },
        {
            title: 'Customer',
            dataIndex: 'customerName',
            key: 'customerName',
            width: 120,
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <Text style={{ fontSize: 12 }}>{text}</Text>
                </Tooltip>
            )
        },
        {
            title: 'Email',
            dataIndex: 'customerEmail',
            key: 'customerEmail',
            width: 150,
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <Text style={{ fontSize: 11 }}>{text}</Text>
                </Tooltip>
            )
        },
        {
            title: 'Items',
            dataIndex: 'orderItems',
            key: 'itemCount',
            width: 60,
            align: 'center',
            render: (orderItems) => (
                <Tag color="blue">{orderItems.length}</Tag>
            )
        }
    ];

    return (
        <Modal
            title={
                <Space>
                    <MailOutlined style={{ color: '#1890ff' }} />
                    <span>Send Custom Message via Email</span>
                </Space>
            }
            visible={visible}
            onCancel={handleCancel}
            width={1200}
            footer={[
                <Button key="cancel" onClick={handleCancel} disabled={sending}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    icon={<SendOutlined />}
                    loading={sending}
                    onClick={handleSendMessage}
                    disabled={!messageContent.trim() || sending}
                >
                    Send Email to {groupedOrders.length} Order{groupedOrders.length > 1 ? 's' : ''}
                </Button>
            ]}
        >
            <Row gutter={16}>
                {/* Left Column - Order Summary */}
                <Col span={14}>
                    <div>
                        <Text strong style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>
                            Selected Orders: {groupedOrders.length}
                        </Text>
                        <Table
                            columns={columns}
                            dataSource={groupedOrders}
                            rowKey="shopifyOrderId"
                            pagination={false}
                            size="small"
                            scroll={{ y: 500 }}
                        />
                    </div>
                </Col>

                {/* Right Column - Message Composition & Preview */}
                <Col span={10}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {/* Message Composition */}
                        <div>
                            <Text strong style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>
                                Custom Message Content:
                            </Text>
                            
                            <TextArea
                                ref={textAreaRef}
                                value={messageContent}
                                onChange={(e) => setMessageContent(e.target.value)}
                                placeholder="Enter your custom message here. The store name will be automatically included in the email greeting and closing."
                                rows={6}
                                maxLength={5000}
                                showCount
                                disabled={sending}
                                style={{ fontSize: 13, marginBottom: 12 }}
                            />
                            
                            <Text strong style={{ fontSize: 13, marginBottom: 8, display: 'block' }}>
                                📧 Email Preview:
                            </Text>
                            <div style={{ 
                                background: '#fafafa', 
                                padding: 16, 
                                borderRadius: 4, 
                                fontSize: 13,
                                border: '1px solid #d9d9d9',
                                fontFamily: 'Arial, sans-serif',
                                lineHeight: '1.6',
                                maxHeight: 400,
                                overflowY: 'auto'
                            }}>
                                <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e8e8e8' }}>
                                    <Text strong style={{ fontSize: 12, color: '#666' }}>Subject: </Text>
                                    <Text style={{ fontSize: 12 }}>
                                        {groupedOrders[0]?.storeName || '[Store Name]'} - Thank You for Your Order
                                    </Text>
                                </div>
                                
                                <div style={{ color: '#333' }}>
                                    <p style={{ margin: '0 0 12px 0' }}>Hi,</p>
                                    
                                    <p style={{ margin: '0 0 12px 0' }}>
                                        Thank you so much for shopping with <strong>{groupedOrders[0]?.storeName || '[Store Name]'}</strong> — we truly appreciate your support!
                                    </p>
                                    
                                    {messageContent.trim() ? (
                                        <>
                                            {messageContent.split('\n').map((line, idx) => (
                                                <p key={idx} style={{ margin: '12px 0' }}>
                                                    {line || '\u00A0'}
                                                </p>
                                            ))}
                                        </>
                                    ) : (
                                        <p style={{ margin: '12px 0', fontStyle: 'italic', color: '#999' }}>
                                            [Your custom message will appear here]
                                        </p>
                                    )}
                                    
                                    <p style={{ margin: '16px 0 0 0' }}>
                                        Thank you again for being part of the <strong>{groupedOrders[0]?.storeName || '[Store Name]'}</strong> community!
                                    </p>
                                    
                                    <p style={{ margin: '12px 0 0 0' }}>Warm regards,</p>
                                    <p style={{ margin: '4px 0 0 0' }}>
                                        The <strong>{groupedOrders[0]?.storeName || '[Store Name]'}</strong> Team
                                    </p>
                                </div>
                            </div>
                            
                            <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                                <MessageOutlined /> The email will be sent from the store's configured email address to each customer.
                            </Text>
                        </div>
                    </Space>
                </Col>
            </Row>
        </Modal>
    );
};

export default SendCustomMessageModal;
